import api from './api';

/**
 * Channel partner portal, phase 1. Read-only views of the partner's OWN agents, plus the invite
 * flow. There is no money surface here for the partner: the only figure is the commission the
 * agent themselves earned, as the server already holds it.
 */

export interface PartnerProfile {
  id: string;
  name: string;
  code: string;
  /** What the broker calls this kind of party ("Franchise", "DSA", ...), else "Partner". */
  partner_type: string;
  email: string;
  phone: string;
  broker_name: string;
  posp_count: number;
}

export interface PartnerPosp {
  id: string;
  posp_code: string;
  name: string;
  status: string;
  status_display: string;
  activated_at: string | null;
  posp_commission_earned: string;
}

export interface PartnerProductionRow {
  posp_id: string;
  posp_code: string;
  name: string;
  policy_count: number;
  premium: string;
  posp_commission_earned: string;
  by_insurer: { insurer: string; policy_count: number; premium: string }[];
}

export interface PartnerTarget {
  id: string;
  posp_id: string;
  posp_code: string;
  name: string;
  target_type: string;
  period_type: string;
  start_date: string;
  end_date: string;
  target_value: string;
  achieved_value: string;
  achievement_percentage: string;
}

export interface PartnerInvite {
  id: string;
  name: string;
  mobile: string;
  email: string;
  invited_at: string;
  expires_at: string;
  stage: string;
  stage_display: string;
  /** Present once, on the create response only. */
  invite_url?: string;
}

export interface InviteInfo {
  purpose: 'partner_login' | 'posp_application' | 'posp_login';
  broker_name: string;
  invited_by: string;
  prospect_name: string;
  expires_at: string;
}

export interface ProspectApplication {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: 'male' | 'female' | 'other';
  address_line1: string;
  pincode: string;
  pan_number: string;
  aadhaar_number?: string;
  bank_name: string;
  bank_account_number: string;
  bank_ifsc_code: string;
  bank_account_holder_name: string;
}

export interface PartnerLoginStatus {
  has_login: boolean;
  status: 'active' | 'revoked' | null;
  email: string | null;
  eligible: boolean;
}

const unwrap = <T,>(data: { results: T[] }) => data.results;

export const partnerPortalApi = {
  me: async () => (await api.get<PartnerProfile>('/partner/me/')).data,
  posps: async () => unwrap((await api.get<{ results: PartnerPosp[] }>('/partner/posps/')).data),
  production: async (period?: string) =>
    (await api.get<{ period: string; results: PartnerProductionRow[] }>('/partner/production/', { params: { period } }))
      .data,
  targets: async () => unwrap((await api.get<{ results: PartnerTarget[] }>('/partner/targets/')).data),
  invites: async () => unwrap((await api.get<{ results: PartnerInvite[] }>('/partner/invites/')).data),
  createInvite: async (body: { name: string; mobile: string; email: string }) =>
    (await api.post<PartnerInvite>('/partner/invites/', body)).data,
  revokeInvite: async (id: string) => {
    await api.delete(`/partner/invites/${id}/`);
  },
};

/** Public (no session) endpoints reached from an emailed or shared link. */
export const partnerInviteApi = {
  info: async (token: string) => (await api.get<InviteInfo>(`/brokers/partner-invites/${token}/`)).data,
  apply: async (token: string, body: ProspectApplication) =>
    (await api.post<{ detail: string }>(`/brokers/partner-invites/${token}/application/`, body)).data,
  setPassword: async (token: string, password: string) =>
    (await api.post<{ detail: string }>(`/brokers/partner-invites/${token}/set-password/`, { password })).data,
};

/** Broker admin: manage the login of one partner channel. */
export const partnerLoginAdminApi = {
  status: async (channelId: string) =>
    (await api.get<PartnerLoginStatus>(`/brokers/broker-channels/${channelId}/partner-login/`)).data,
  create: async (channelId: string, body: { email: string; name?: string; mobile?: string }) =>
    (await api.post<{ status: string; invite_url: string }>(`/brokers/broker-channels/${channelId}/partner-login/`, body))
      .data,
  resend: async (channelId: string) =>
    (await api.post<{ status: string; invite_url: string }>(`/brokers/broker-channels/${channelId}/partner-login/?resend=1`))
      .data,
  revoke: async (channelId: string) => {
    await api.delete(`/brokers/broker-channels/${channelId}/partner-login/`);
  },
};
